import { clearLocalStorage } from './config/localStorage';
import { clearSessionStorage } from './config/sessionStorage';
import { clearNavigationState } from './navigationState';
import { logoutUser } from '../actions/authActions';
import { USER_ACTION_TYPES, CREATOR_ACTION_TYPES } from '../actions/types';
import { ROUTES } from '../constants/routes';
import { AUTH_TOKEN_KEYS } from '../constants/common';
import authService from '../services/authService';
import { showLoading, closeAlert } from './swalHandler';
import { resetFcmSyncSession } from '../pusNotification/fcm/fcmSyncCoordinator';

const dispatchAccessTokenRemoved = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent('localStorageChange', {
      detail: { key: AUTH_TOKEN_KEYS.ACCESS_TOKEN, value: null, action: 'remove' },
    })
  );
};

/**
 * Clears local session storage. Used before hard redirect so PrivateRoute does not
 * re-render protected pages while React is still mounted.
 */
const clearLocalSession = () => {
  clearNavigationState();
  resetFcmSyncSession();
  clearLocalStorage();
  clearSessionStorage();
  dispatchAccessTokenRemoved();
};

/**
 * Clears all user state including navigation, storage, and Redux state
 * This function performs a complete logout cleanup
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} [options]
 * @param {boolean} [options.redirectToLogin=false] - Hard-navigate to login without
 *   Redux updates (avoids React DOM errors while unmounting protected routes)
 * @returns {Promise<void>}
 */
export const clearAllUserState = async (dispatch, options = {}) => {
  const { redirectToLogin = false } = options;

  if (redirectToLogin) {
    try {
      clearNavigationState();
      await authService.logout();
    } catch {
      // authService.logout clears tokens even when the API fails
    } finally {
      clearLocalSession();
      window.location.assign(ROUTES.LOGIN);
    }
    return;
  }

  showLoading('Logging out...');

  try {
    clearNavigationState();
    await dispatch(logoutUser());
    resetFcmSyncSession();
    clearLocalStorage();
    clearSessionStorage();
    dispatchAccessTokenRemoved();

    dispatch({
      type: USER_ACTION_TYPES.DELETE_USER_SUCCESS,
      payload: { message: 'User cleared on logout' }
    });

    dispatch({
      type: CREATOR_ACTION_TYPES.DELETE_CREATOR_SUCCESS,
      payload: { message: 'Creator cleared on logout' }
    });
  } catch (error) {
    clearLocalSession();
    throw error;
  } finally {
    closeAlert();
  }
};

